import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, ShoppingCart, Plus, Minus, Search } from 'lucide-react';
import BottomNavigation from '../../components/common/BottomNavigation';
import { useApp } from '../../context/AppContext';
import { useData } from '../../context/DataContext';
import { useCart } from '../../context/CartContext';

const CustomerProducts: React.FC = () => {
  const { setCurrentScreen } = useApp();
  const { products } = useData();
  const { cart, addToCart, removeFromCart, getCartItemCount, getCartTotal, getCartItems, clearCart } = useCart();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('All');

  // Debug logging
  console.log('🛍️ CustomerProducts: Products loaded:', products);
  console.log('🛍️ CustomerProducts: Cart state:', cart);
  console.log('🛍️ CustomerProducts: Cart total:', getCartTotal(products));
  console.log('🛍️ CustomerProducts: Cart items:', getCartItems(products));
  
  // Debug function to check cart state
  const debugCart = () => {
    console.log('🔍 DEBUG CART:');
    console.log('Cart object:', cart);
    console.log('Cart keys:', Object.keys(cart));
    console.log('Cart values:', Object.values(cart));
    console.log('Products with IDs:', products.map(p => ({ id: p.id, name: p.name, price: p.price })));
  };

  // Debug function to inspect product IDs
  React.useEffect(() => {
    console.log('🛍️ CustomerProducts: Inspecting loaded product IDs:');
    products.forEach((p, index) => {
      console.log(`  Product ${index}: ID="${p.id}", Name="${p.name}", Price=${p.price}`);
    });
  }, [products]);

  const handleBack = () => {
    setCurrentScreen('customer-home');
  };


  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || product.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const categories = ['All', ...Array.from(new Set(products.map(p => p.category)))];

  const ProductCard: React.FC<{ product: any; index: number }> = ({ product, index }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
      className="bg-slate-800 rounded-lg p-4 border border-slate-700"
    >
      <div className="flex items-start space-x-4">
        <img
          src={product.image}
          alt={product.name}
          className="w-20 h-20 rounded-lg object-cover flex-shrink-0"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between mb-2">
            <div className="flex-1 min-w-0">
              <h3 className="text-white font-medium text-sm leading-tight mb-1">{product.name}</h3>
              <p className="text-slate-400 text-xs leading-relaxed line-clamp-3 mb-2">
                {product.description}
              </p>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-teal-400 font-semibold text-lg">₹{product.price}</p>
              <p className="text-slate-500 text-xs">Stock: {product.stock}</p>
            </div>
            <div className="flex-shrink-0 ml-3">
              {cart[product.id] ? (
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => {
                      console.log('🛍️ CustomerProducts: Removing from cart - Product ID:', product.id);
                      console.log('🛍️ CustomerProducts: Product object:', product);
                      removeFromCart(product.id);
                    }}
                    className="w-8 h-8 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center"
                  >
                    <Minus size={16} />
                  </button>
                  <span className="text-white font-medium w-8 text-center text-sm">{cart[product.id]}</span>
                  <button
                    onClick={() => {
                      console.log('🛍️ CustomerProducts: Adding to cart (plus button) - Product ID:', product.id);
                      console.log('🛍️ CustomerProducts: Product object:', product);
                      addToCart(product.id, products);
                    }}
                    className="w-8 h-8 bg-teal-500 hover:bg-teal-600 text-white rounded-full flex items-center justify-center"
                  >
                    <Plus size={16} />
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => {
                    console.log('🛍️ CustomerProducts: Adding to cart (initial button) - Product ID:', product.id);
                    console.log('🛍️ CustomerProducts: Product object:', product);
                    addToCart(product.id, products);
                  }}
                  className="bg-teal-500 hover:bg-teal-600 text-white px-3 py-2 rounded-lg text-xs font-medium transition-colors duration-200 whitespace-nowrap"
                >
                  Add to Cart
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-slate-900 pb-24">
      {/* Header */}
      <div className="flex items-center justify-between p-4 bg-slate-800 border-b border-slate-700">
        <div className="flex items-center">
          <button
            onClick={handleBack}
            className="p-2 text-slate-400 hover:text-white mr-3"
          >
            <ArrowLeft size={20} />
          </button>
          <h1 className="text-lg font-bold text-white">Products</h1>
        </div>
        <div className="flex items-center space-x-1">
          <button 
            onClick={debugCart}
            className="px-2 py-1 bg-red-500 text-white text-xs rounded"
          >
            Debug
          </button>
          <button 
            onClick={clearCart}
            className="px-2 py-1 bg-orange-500 text-white text-xs rounded"
          >
            Clear
          </button>
          <button className="p-2 text-slate-400 hover:text-white relative">
            <ShoppingCart size={20} />
            {getCartItemCount() > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                {getCartItemCount()}
              </span>
            )}
          </button>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Search Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative"
        >
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
            placeholder="Search products..."
          />
        </motion.div>

        {/* Category Filter */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="flex space-x-2 overflow-x-auto pb-2"
        >
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors duration-200 ${
                selectedCategory === category
                  ? 'bg-teal-500 text-white'
                  : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
              }`}
            >
              {category}
            </button>
          ))}
        </motion.div>

        {/* Products List */}
        <div className="space-y-4">
          {filteredProducts.map((product, index) => (
            <ProductCard key={product.id || `product-${index}`} product={product} index={index} />
          ))}
        </div>

        {/* Cart Summary */}
        {getCartItemCount() > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-slate-800 rounded-lg p-4 border border-slate-700 shadow-lg mb-4"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white font-medium">{getCartItemCount()} items in cart</p>
                <p className="text-teal-400 font-semibold">Total: ₹{getCartTotal(products).toFixed(2)}</p>
              </div>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="bg-teal-500 hover:bg-teal-600 text-white font-semibold py-2 px-6 rounded-lg transition-colors duration-200"
              >
                Checkout
              </motion.button>
            </div>
          </motion.div>
        )}
      </div>

      <BottomNavigation />
    </div>
  );
};

export default CustomerProducts;
